import { request } from 'hooks/useAppApi';
import { listDraftIds, loadDraft, clearDraft } from 'utils/offlineMantencionStore';
import { buildMantencionPayload } from 'utils/buildMantencionPayload';
import { listPhotosForMantencion, deletePhoto } from 'utils/offlinePhotosStore';
import { presignHallazgoImagenUpload, confirmHallazgoImagen } from 'api/hallazgoImagenes';

let inFlight = false;

const isOk = (res) =>
  !!res && typeof res.status === 'number' && res.status >= 200 && res.status < 300;

// Resuelve el idHallazgo recién creado por el PUT para un `tempId`. El PUT
// /mantenciones/:id devuelve `hallazgos: [{ id_act_res, id_hallazgo, tipo,
// tempId }]` (mismo shape que consume el provider vía mergeHallazgosResponse).
// Multi-hallazgo: la foto se keyea por tempId (la fila del hallazgo), no por
// (idRes, tipo) — un ítem puede tener N hallazgos del mismo tipo.
export const resolveHallazgoId = (hallazgos, tempId) => {
  if (!Array.isArray(hallazgos) || tempId == null) return undefined;
  const found = hallazgos.find((h) => String(h.tempId) === String(tempId));
  return found ? Number(found.id_hallazgo) : undefined;
};

// Sube las fotos pendientes de una mantención a S3, contra el hallazgo recién
// creado por el PUT. Devuelve `true` si todas las que TENÍAN hallazgo subieron
// OK (o no había fotos); `false` si alguna subida falló (para conservar el draft
// y reintentar). Las fotos sin hallazgo resoluble (falta descripción → no se
// creó el hallazgo) se saltean SIN marcar fallo: no se suben pero tampoco
// bloquean el cierre del draft (quedan para limpieza por TTL).
async function uploadOfflinePhotos(mantencionId, hallazgos) {
  const photos = await listPhotosForMantencion(mantencionId);
  if (!photos.length) return true;
  let allOk = true;
  for (const photo of photos) {
    const idHallazgo = resolveHallazgoId(hallazgos, photo.tempId);
    if (!idHallazgo) continue; // sin descripción → sin hallazgo: no se sube, no bloquea
    try {
      // eslint-disable-next-line no-await-in-loop
      const { uploadUrl, key } = await presignHallazgoImagenUpload(idHallazgo, photo.type);
      // eslint-disable-next-line no-await-in-loop
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': photo.type },
        body: photo.blob,
      });
      if (!putRes || !putRes.ok) throw new Error(`S3 PUT failed: ${putRes && putRes.status}`);
      // eslint-disable-next-line no-await-in-loop
      await confirmHallazgoImagen(idHallazgo, key);
      // eslint-disable-next-line no-await-in-loop
      await deletePhoto(photo.localId);
    } catch {
      allOk = false; // conservar binario + draft para reintento
    }
  }
  return allOk;
}

// Recorre todos los drafts pendientes en localStorage y los sube vía PUT.
// Tras un PUT exitoso, sube las fotos pendientes de esa mantención al hallazgo
// recién creado. En éxito (PUT + fotos) limpia el draft; si el PUT o alguna
// foto falla, conserva el draft para el próximo reintento.
// Idempotente y reentrante-seguro (guard `inFlight`).
export async function flushPendingDrafts() {
  if (inFlight) return { synced: [], failed: [], skipped: true };
  inFlight = true;
  const synced = [];
  const failed = [];
  try {
    const ids = listDraftIds();
    for (const id of ids) {
      const draft = loadDraft(id);
      if (!draft) continue;
      const payload = buildMantencionPayload(draft);
      // eslint-disable-next-line no-await-in-loop
      const res = await request(`/mantenciones/${id}`, 'put', payload);
      if (!isOk(res)) {
        failed.push(id);
        continue;
      }
      // eslint-disable-next-line no-await-in-loop
      const photosOk = await uploadOfflinePhotos(id, res.data && res.data.hallazgos);
      if (photosOk) {
        clearDraft(id);
        synced.push(id);
      } else {
        failed.push(id);
      }
    }
  } finally {
    inFlight = false;
  }
  return { synced, failed };
}
